import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const LOCKOUT_MS = 5 * 60 * 1000;
const MAX_ATTEMPTS = 5;

function attemptKey(email: string) {
  return `klarr_magic_attempts_${email.toLowerCase().trim()}`;
}

function lockoutKey(email: string) {
  return `klarr_magic_lockout_${email.toLowerCase().trim()}`;
}

function secondsUntilUnlocked(email: string): number {
  const stored = localStorage.getItem(lockoutKey(email));
  if (!stored) return 0;
  const remaining = LOCKOUT_MS - (Date.now() - parseInt(stored, 10));
  if (remaining <= 0) {
    localStorage.removeItem(lockoutKey(email));
    localStorage.setItem(attemptKey(email), '0');
    return 0;
  }
  return Math.ceil(remaining / 1000);
}

function recordAttempt(email: string): void {
  const count = parseInt(localStorage.getItem(attemptKey(email)) ?? '0', 10) + 1;
  localStorage.setItem(attemptKey(email), String(count));
  if (count >= MAX_ATTEMPTS) {
    localStorage.setItem(lockoutKey(email), Date.now().toString());
  }
}

export function LoginPage() {
  const {
    user,
    collegeAdmin,
    firmAdmin,
    coach,
    loading,
    accessDenied,
    pendingMagicLink,
    magicLinkInProgress,
    magicLinkError,
    signInWithGoogle,
    sendMagicLink,
    completeMagicLink,
  } = useAuth();

  const [signingIn, setSigningIn] = useState(false);
  const [googleError, setGoogleError] = useState('');

  const [magicEmail, setMagicEmail] = useState('');
  const [sendingLink, setSendingLink] = useState(false);
  const [linkSent, setLinkSent] = useState(false);
  const [linkError, setLinkError] = useState('');

  const [completeEmail, setCompleteEmail] = useState('');
  const [completing, setCompleting] = useState(false);

  if (loading || magicLinkInProgress) {
    return (
      <div style={{ ...outerStyle }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--accent-text)', marginBottom: 16, letterSpacing: '0.04em' }}>
            KLARR
          </div>
          <div style={{ color: 'var(--text-secondary)', fontSize: 14 }}>
            {magicLinkInProgress ? 'Signing you in…' : 'Loading…'}
          </div>
        </div>
      </div>
    );
  }
  if (user || collegeAdmin || firmAdmin || coach) return <Navigate to="/dashboard" replace />;

  const handleGoogleSignIn = async () => {
    setSigningIn(true);
    setGoogleError('');
    try {
      await signInWithGoogle();
    } catch (err: any) {
      if (err.code !== 'auth/popup-closed-by-user') {
        setGoogleError('Sign-in failed. Please try again.');
      }
    } finally {
      setSigningIn(false);
    }
  };

  const handleSendLink = async (e: React.FormEvent) => {
    e.preventDefault();
    const email = magicEmail.trim().toLowerCase();
    if (!email) return;

    setLinkError('');
    setLinkSent(false);

    const wait = secondsUntilUnlocked(email);
    if (wait > 0) {
      const mins = Math.ceil(wait / 60);
      setLinkError(
        `Too many attempts. Please wait ${mins} minute${mins !== 1 ? 's' : ''} before requesting another link for this address.`
      );
      return;
    }

    setSendingLink(true);
    try {
      await sendMagicLink(email);
    } catch {
      // Intentional: show generic message regardless to prevent enumeration.
    } finally {
      recordAttempt(email);
      setSendingLink(false);
      setLinkSent(true);
      setMagicEmail('');
    }
  };

  const handleCompleteMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    const email = completeEmail.trim().toLowerCase();
    if (!email) return;
    setCompleting(true);
    await completeMagicLink(email);
    setCompleting(false);
  };

  // ── "Complete sign-in" screen (opened link in a different browser) ─────────
  if (pendingMagicLink) {
    return (
      <div style={outerStyle}>
        <div className="card" style={{ width: 380 }}>
          <Brand />
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20, lineHeight: 1.6 }}>
            Enter the email address you used to request this login link to complete sign-in.
          </p>
          {magicLinkError && (
            <div className="error-message" style={{ marginBottom: 14 }}>{magicLinkError}</div>
          )}
          <form onSubmit={handleCompleteMagicLink}>
            <div className="form-group" style={{ marginBottom: 12 }}>
              <label className="form-label">Email address</label>
              <input
                className="form-input"
                type="email"
                value={completeEmail}
                onChange={(e) => setCompleteEmail(e.target.value)}
                placeholder="you@company.com"
                autoComplete="email"
                autoFocus
              />
            </div>
            <button
              type="submit"
              className="btn-primary"
              disabled={completing || !completeEmail.trim()}
              style={{ width: '100%', padding: '11px 20px', fontSize: 14 }}
            >
              {completing ? 'Signing in...' : 'Complete Sign-In'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ── Normal login screen ────────────────────────────────────────────────────
  return (
    <div style={outerStyle}>
      <div className="card" style={{ width: 380 }}>
        <Brand />

        {accessDenied && (
          <div className="error-message" style={{ marginBottom: 20 }}>
            Access denied. This account does not have admin permissions.
          </div>
        )}
        {magicLinkError && (
          <div className="error-message" style={{ marginBottom: 20 }}>{magicLinkError}</div>
        )}

        {/* Google Sign-In */}
        {googleError && (
          <div className="error-message" style={{ marginBottom: 12 }}>{googleError}</div>
        )}
        <button
          className="btn-primary"
          onClick={handleGoogleSignIn}
          disabled={signingIn}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
            padding: '12px 20px',
            fontSize: 15,
          }}
        >
          <GoogleIcon />
          {signingIn ? 'Signing in...' : 'Sign in with Google'}
        </button>

        {/* Divider */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '20px 0' }}>
          <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>or</span>
          <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
        </div>

        {/* Magic link send form */}
        {linkSent ? (
          <>
            <div
              style={{
                padding: '14px 16px',
                background: 'var(--accent-tint)',
                border: '1px solid rgba(0, 232, 122, 0.25)',
                borderRadius: 'var(--radius-md)',
                fontSize: 13,
                color: 'var(--text-primary)',
                lineHeight: 1.6,
              }}
            >
              If this email is registered, a login link has been sent. Check your inbox and click
              the link to sign in.
            </div>
            <button
              style={{
                marginTop: 12,
                background: 'none',
                border: 'none',
                color: 'var(--text-muted)',
                fontSize: 12,
                cursor: 'pointer',
                width: '100%',
              }}
              onClick={() => setLinkSent(false)}
            >
              Use a different email
            </button>
          </>
        ) : (
          <form onSubmit={handleSendLink}>
            <div className="form-group" style={{ marginBottom: 10 }}>
              <label className="form-label">Email address</label>
              <input
                className="form-input"
                type="email"
                value={magicEmail}
                onChange={(e) => { setMagicEmail(e.target.value); setLinkError(''); }}
                placeholder="you@company.com"
                autoComplete="email"
              />
            </div>
            {linkError && (
              <div className="error-message" style={{ marginBottom: 10 }}>{linkError}</div>
            )}
            <button
              type="submit"
              className="btn-secondary"
              disabled={sendingLink || !magicEmail.trim()}
              style={{ width: '100%', padding: '11px 20px', fontSize: 14 }}
            >
              {sendingLink ? 'Sending...' : 'Send Login Link'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

const outerStyle: React.CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'var(--bg-primary)',
};

function Brand() {
  return (
    <div style={{ textAlign: 'center', marginBottom: 36 }}>
      <div style={{ fontSize: 32, fontWeight: 800, letterSpacing: '0.04em', marginBottom: 8, color: 'var(--accent-text)' }}>
        KLARR
      </div>
      <div style={{ fontSize: 13, color: 'var(--text-secondary)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
        Partner Portal
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#0A0A0F" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#0A0A0F" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#0A0A0F" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#0A0A0F" />
    </svg>
  );
}
